import createFetchClient from "openapi-fetch";
import type { Client } from "openapi-fetch";
import type { paths } from "../generated/api";

export interface RestomenuClientOptions {
  /**
   * База REST, например https://api.dev.restomenu.cc/rest/v1
   * Приходит параметром: core не читает переменные окружения приложений.
   */
  baseUrl: string;
  /** Язык сообщений об ошибках. Дефолт бэка тоже `ru`, но полагаться на него нельзя. */
  language?: "ru" | "en";
  /**
   * Сессия кончилась. Зовётся ОДИН раз на пачку параллельных 401.
   * Гасить сессию (`DELETE /session`) — обязанность приложения, не транспорта:
   * иначе получается цикл 401 → DELETE → 401.
   * Флаг single-flight сбрасывается успешным (<400) ответом ЧЕРЕЗ ЭТОТ ЖЕ
   * экземпляр: re-auth обязан идти этим же клиентом, иначе следующий
   * настоящий 401 останется недоложенным.
   */
  onUnauthenticated?: () => void;
  /** Базовая пауза между повторами; растёт экспоненциально. Параметр ради тестов. */
  retryDelayMs?: number;
  /** Подменить транспорт. Нужен для тестов и для нестандартных сред. */
  fetch?: typeof fetch;
}

const RETRY_ATTEMPTS = 2;
const RETRY_DELAY_MS = 300;
const IDEMPOTENT = new Set(["GET", "HEAD"]);

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function createRestomenuClient(options: RestomenuClientOptions): Client<paths> {
  const { baseUrl, language = "ru", onUnauthenticated, retryDelayMs = RETRY_DELAY_MS } = options;

  // Транспорт фиксируется при создании клиента. Повтор обязан идти через него,
  // а не через глобальный fetch напрямую: иначе инжектированный транспорт
  // будет молча обойдён.
  const doFetch: typeof fetch = options.fetch ?? ((...args) => globalThis.fetch(...args));

  // Single-flight: пачка параллельных запросов, получивших 401, обязана
  // разбудить обработчик один раз, иначе экран из пяти запросов даёт
  // пять сбросов сессии и пять редиректов.
  let unauthenticatedNotified = false;

  const client = createFetchClient<paths>({
    baseUrl,
    credentials: "include",
    headers: { "Accept-Language": language },
    fetch: doFetch,
  });

  // Ретрай живёт здесь, а не в onResponse: сетевой сбой — это reject у fetch,
  // до onResponse он не доходит вовсе. Один код на оба случая — 5xx и обрыв.
  async function retryIdempotent(request: Request): Promise<Response | undefined> {
    if (!IDEMPOTENT.has(request.method)) return undefined;
    // Пауза не слушает abort signal: отменённый запрос дожёвывает ретраи
    // в фоне (<1 с при дефолтной задержке). Осознанная простота.
    for (let attempt = 0; attempt < RETRY_ATTEMPTS; attempt++) {
      await sleep(retryDelayMs * 2 ** attempt); // экспоненциальный backoff
      try {
        const retried = await doFetch(request.clone());
        if (retried.status < 500) return retried;
      } catch {
        // сеть всё ещё лежит — идём на следующую попытку
      }
    }
    return undefined;
  }

  function noteStatus(status: number): void {
    if (status === 401) {
      if (!unauthenticatedNotified) {
        unauthenticatedNotified = true;
        onUnauthenticated?.();
      }
    } else if (status < 400) {
      unauthenticatedNotified = false;
    }
  }

  client.use({
    async onResponse({ request, response }) {
      // POST/PATCH/DELETE не повторяем НИКОГДА: домен не идемпотентен,
      // слепой повтор задваивает создание товара.
      if (response.status >= 500) {
        const retried = await retryIdempotent(request);
        if (retried) {
          // Статус повтора тоже проходит проверку 401 — иначе сессия,
          // истёкшая между попытками, останется незамеченной.
          noteStatus(retried.status);
          return retried;
        }
      }
      noteStatus(response.status);
      return response;
    },

    async onError({ request }) {
      const retried = await retryIdempotent(request);
      if (retried) {
        noteStatus(retried.status);
        return retried;
      }
      // Ничего не возвращаем — openapi-fetch перебросит исходную ошибку как есть
      // (см. его src/index.js: falsy результат onError = «не обработано»).
      // Вернуть сюда сам `error` нельзя: контракт onError разрешает только
      // Response или Error, а на любой другой truthy рантайм бросает
      // «onError: must return new Response() or instance of Error»,
      // подменяя настоящую причину сбоя.
      return undefined;
    },
  });

  return client;
}
